// src/pages/ReportIssue/hooks/useIssueSubmission.js
import React, {useState, useCallback} from "react";
import EditorState from "draft-js/lib/EditorState";
import {convertToRaw} from "draft-js";
import {useNavigate} from "react-router-dom";
import {ApiDetails, KEY} from "../../../dummyData";
import CryptoJS from "crypto-js";

const useIssueSubmission = (props, tenantDetails) => {

    // Decrypt tenant details from localStorage
    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );

    let originalText = details.toString(CryptoJS.enc.Utf8);

    const navigate = useNavigate();

    // State declarations
    const [problemTypes, setProblemTypes] = useState([]);
    const [issues, setIssues] = useState([]);
    const [selectedProblemType, setSelectedProblemType] = useState("");
    const [selectedIssue, setSelectedIssue] = useState("");
    const [selectedPriority, setSelectedPriority] = useState("Medium");
    const [loading, setLoading] = useState(false);
    const [loadingProblemTypes, setLoadingProblemTypes] = useState(true);
    const [loadingIssues, setLoadingIssues] = useState(false);
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [photos, setPhotos] = useState([]);

    // Show snackbar - either using props.snackBar or a local function
    const showSnackBar = (text, type) => {
        if (props.snackBar) {
            props.snackBar({text, type});
        } else {
            setSubmitError(type === "error");
            setErrorMessage(text);
        }
    };

    // Fetch problem types from API
    const fetchProblemTypes = useCallback(() => {
        setLoadingProblemTypes(true);

        const formData = new FormData();
        formData.append("tenant_id", tenantDetails.get("tenant_id"));

        fetch(ApiDetails + "pegasus/visionary/tenant/app/problemTypes", {
            method: "POST",
            headers:{
                "token": new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("access_token"),
            },
            body: formData,
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 403) {
                    // Session expired - redirect to login
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }

                if (response.status === 200) {
                    setProblemTypes(data);
                } else {
                    showSnackBar("Failed fetching issue types.", "error");
                }
                setLoadingProblemTypes(false);
            })
            .catch((err) => {
                console.log(err);
                showSnackBar("Something went wrong. Try again later", "error");
                setLoadingProblemTypes(false);
            });
    }, [tenantDetails]);

    // Fetch specific issues based on problem type
    const getIssues = useCallback((value) => {
        setLoadingIssues(true);
        setIssues([]);

        // Fix: Use FormData consistently instead of JSON
        const formData = new FormData();
        formData.append("problem_type", value);

        fetch(ApiDetails + "pegasus/visionary/tenant/app/problemTypes/types", {
            method: "POST",
            headers:{
                "token": new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("access_token"),
            },
            body: JSON.stringify({
                problem_type: value,
            }),// Use FormData instead of JSON
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 403) {
                    // Session expired - redirect to login
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }

                if (response.status === 200) {
                    setIssues(data);
                } else {
                    showSnackBar("Something went wrong. Try again later", "error");
                }
                setLoadingIssues(false);
            })
            .catch((err) => {
                console.log(err);
                showSnackBar("Something went wrong. Try again later", "error");
                setLoadingIssues(false);
            });
    }, []);

    // Handle photo upload
    const handlePhotoUpload = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);

            // Basic validation - max 3 files, each under 5MB
            if (photos.length + filesArray.length > 3) {
                showSnackBar("You can upload a maximum of 3 photos", "error");
                return;
            }

            const oversizedFiles = filesArray.filter(file => file.size > 5 * 1024 * 1024);
            if (oversizedFiles.length > 0) {
                showSnackBar("One or more files exceed the 5MB size limit", "error");
                return;
            }

            // Process files
            const newPhotos = filesArray.map(file => ({
                file,
                preview: URL.createObjectURL(file),
                name: file.name
            }));

            setPhotos(prev => [...prev, ...newPhotos]);
        }
    };

    // Remove photo
    const removePhoto = (index) => {
        setPhotos(prev => {
            const updated = [...prev];
            // Revoke the object URL to avoid memory leaks
            URL.revokeObjectURL(updated[index].preview);
            updated.splice(index, 1);
            return updated;
        });
    };

    // Submit issue to API using FormData
    const submitIssue = async (tenantDetails) => {
        setIsSubmitting(true);
        setLoading(true);

        try {
            const formData = new FormData();

            // Append tenant and property data
            formData.append("tenant_id", tenantDetails.get("tenant_id"));
            formData.append("landlord_id", tenantDetails.get("landlord_id"));
            formData.append("unit", tenantDetails.get("house_number"));
            formData.append("property_id", tenantDetails.get("property_id"));
            formData.append("house_number", tenantDetails.get("house_number"));
            formData.append("phone_number", tenantDetails.get("phone_number"));

            // Append issue data
            formData.append("problem_type", selectedProblemType);
            formData.append("problem", selectedIssue);
            formData.append("priority", selectedPriority);
            formData.append("description", JSON.stringify(
                convertToRaw(editorState.getCurrentContent())
            ));

            // Append status and other standard fields
            formData.append("status", "OPEN");
            formData.append("is_internal", "false");

            // Append photos - using the field name 'file' for consistency with UploadTicketAttachment
            photos.forEach((photo, index) => {
                // Use 'file' for the first photo to match UploadTicketAttachment
                if (index === 0) {
                    formData.append("file", photo.file);
                } else {
                    // Use photo_1, photo_2, etc. for additional photos
                    formData.append(`photo_${index}`, photo.file);
                }
            });

            fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/create", {
                method: "POST",
            headers:{
                "token": new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("access_token"),
            },
                body: formData,
            })
                .then(async (response) => {
                    if (response.status === 403) {
                    // Session expired - redirect to login
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }

                if (response.status === 200) {
                        setSubmitSuccess(true);
                        showSnackBar("Issue logged successfully", "success");
                        // Reset form
                        setSelectedProblemType("");
                        setSelectedIssue("");
                        setSelectedPriority("Medium");
                        setEditorState(EditorState.createEmpty());
                        setPhotos([]);

                        // Redirect after delay
                        setTimeout(() => {
                            navigate('/tickets');
                        }, 3000);
                    } else {
                        setSubmitError(true);
                        setErrorMessage("Failed to log issue. Please try again.");
                        showSnackBar("Failed to log issue.", "error");
                    }
                    setLoading(false);
                    setIsSubmitting(false);
                })
                .catch((err) => {
                    console.log(err);
                    setSubmitError(true);
                    setErrorMessage("Something went wrong. Please try again later.");
                    showSnackBar("Something went wrong. Try again later", "error");
                    setLoading(false);
                    setIsSubmitting(false);
                });
        } catch (error) {
            console.error("Error submitting issue:", error);
            setSubmitError(true);
            setErrorMessage("Something went wrong. Please try again later.");
            showSnackBar("Something went wrong. Try again later", "error");
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    // Reset form
    const handleReset = () => {
        setSelectedProblemType("");
        setSelectedIssue("");
        setSelectedPriority("Medium");
        setEditorState(EditorState.createEmpty());
        setPhotos([]);
        setSubmitSuccess(false);
        setSubmitError(false);
    };

    // Clean up resources when component unmounts
    React.useEffect(() => {
        return () => {
            // Clean up any object URLs to avoid memory leaks
            photos.forEach(photo => {
                URL.revokeObjectURL(photo.preview);
            });
        };
    }, [photos]);

    return {
        problemTypes,
        issues,
        selectedProblemType,
        selectedIssue,
        selectedPriority,
        editorState,
        photos,
        loading,
        loadingProblemTypes,
        loadingIssues,
        isSubmitting,
        submitSuccess,
        submitError,
        errorMessage,
        setSelectedProblemType,
        setSelectedIssue,
        setSelectedPriority,
        setEditorState,
        setSubmitSuccess,  // Export state setter
        setSubmitError,    // Export state setter
        fetchProblemTypes,
        getIssues,
        handlePhotoUpload,
        removePhoto,
        submitIssue,
        handleReset,
        showSnackBar
    };
};

export default useIssueSubmission;